import {Component, Input} from '@angular/core';
import {DependencySource} from './dependency-conditions';

/** Shows the selected value prominently while preserving its question and ancestor context. */
@Component({
  selector: 'app-qa-dependency-source',
  template: `
    <ng-container *ngIf="compact; else detailed">
      <ng-container *ngFor="let level of compactLevels; let last = last">
        <span class="qa-source-level" [class.qa-source-leaf]="last">{{level}}</span>
        <span *ngIf="!last" class="qa-source-separator">›</span>
      </ng-container>
    </ng-container>
    <ng-template #detailed>
      <span class="qa-source-name">{{source?.questionContext?.label || source?.label || fallback}}</span>
      <span *ngIf="source?.questionContext?.ancestors?.length" class="qa-source-ancestors">
        {{source.questionContext.ancestors.join(' › ')}}
      </span>
      <span *ngIf="source?.questionContext && source.questionContext.questionTitle !== source.questionContext.label"
            class="qa-source-question">{{source.questionContext.questionTitle}}</span>
    </ng-template>
  `,
  styleUrls: ['./dependency-source.component.css'],
  host: {'[attr.title]': 'fullTitle', '[class.qa-source-compact]': 'compact'}
})
export class DependencySourceComponent {
  @Input() source: DependencySource;
  @Input() fallback = '';
  @Input() compact = false;

  /** Preserves every hierarchy level and avoids duplicating a variable mapped to the question itself. */
  private get levels(): string[] {
    const context = this.source?.questionContext;
    if (!context) { return [this.source?.label || this.fallback]; }
    if (!context.ancestors.length && context.questionTitle === context.label) { return [context.label]; }
    return [context.questionTitle, ...context.ancestors, context.label];
  }

  /** Limits each level to twenty-five characters including the ellipsis without splitting Unicode code points. */
  public get compactLevels(): string[] {
    return this.levels.map(level => {
      const characters = Array.from(level);
      return characters.length > 25 ? characters.slice(0, 22).join('') + '...' : level;
    });
  }

  /** Provides the complete hierarchy and technical reference without visual truncation. */
  public get fullTitle(): string {
    if (!this.source) { return this.fallback; }
    return `${this.levels.join(' › ')}\n${this.source.path}`;
  }
}
